import type { ReactNode } from 'react';
import type { Column } from '../components/tables/DataTable';
import {
    renderTimestamp,
    renderBoolean,
    renderNestedField,
    renderPhoto,
    renderDocument,
} from '../components/tables';

/**`
 * Column Builder Utility`
 *
 * Provides a fluent chainable API for constructing table column definitions.
 * Dramatically reduces boilerplate by 60-70% compared to manual column objects.
 *
 * @template T - The type of data items in the table
 *
 * @example
 * ```tsx
 * const columns = columnBuilder<SchoolReportDto>()
 *   .text('numeInstitutieInvatamant', 'Nume', 'Denumire...')
 *   .number('codSiiirUnitate', 'Cod SIIIR', 'Cod...')
 *   .nested('adresa.judet', 'Județ', row => row.adresa?.judet)
 *   .timestamp('createdAt', 'Data creare')
 *   .boolean('isActive', 'Activ')
 *   .action('edit', 'Actiuni', (row) => <EditButton onClick={() => edit(row)} />)
 *   .build();
 * ```
 *
 * Before (manual):
 * ```tsx
 * const columns = [
 *   {
 *     key: 'numeInstitutieInvatamant',
 *     label: 'Nume',
 *     sortable: true,
 *     filterable: true,
 *     filterType: 'text',
 *     filterPlaceholder: 'Denumire...'
 *   },
 *   // ... 50 more lines
 * ];
 * ```
 *
 * After (with builder):
 * ```tsx
 * const columns = columnBuilder<SchoolReportDto>()
 *   .text('numeInstitutieInvatamant', 'Nume', 'Denumire...')
 *   // ... 10 more lines
 *   .build();
 * ```
 */
export class ColumnBuilder<T> {
    private columns: Column<T>[] = [];

    /**
     * Add a text column
     *
     * @param key - The field key from the data object
     * @param label - The column header label
     * @param placeholder - Filter placeholder text (optional)
     * @param options - Additional column options to override defaults
     */
    text(
        key: keyof T | string,
        label: string,
        placeholder?: string,
        options?: Partial<Column<T>>
    ): this {
        this.columns.push({
            key: key as string,
            label,
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterPlaceholder: placeholder,
            ...options,
        });
        return this;
    }

    /**
     * Add a number column
     *
     * @param key - The field key from the data object
     * @param label - The column header label
     * @param placeholder - Filter placeholder text (optional)
     * @param options - Additional column options to override defaults
     */
    number(
        key: keyof T | string,
        label: string,
        placeholder?: string,
        options?: Partial<Column<T>>
    ): this {
        this.columns.push({
            key: key as string,
            label,
            sortable: true,
            filterable: true,
            filterType: 'number',
            filterPlaceholder: placeholder,
            // Coloanele de numere se aliniaza la dreapta, cu cifre tabulare — altfel
            // nu se pot compara pe verticala. Se poate suprascrie prin `options`.
            // → docs/design-system/REPORT_POLISH_PLAN.md, constatarea #8
            align: 'right',
            ...options,
        });
        return this;
    }

    /**
     * Add a nested field column (e.g., user.school.name)
     *
     * @param key - The dot-notation field path (e.g., 'user.school.name')
     * @param label - The column header label
     * @param accessor - Function to safely access the nested value
     * @param options - Additional column options to override defaults
     */
    nested(
        key: string,
        label: string,
        accessor: (row: T) => any,
        options?: Partial<Column<T>>
    ): this {
        this.columns.push({
            key,
            label,
            sortable: true,
            filterable: true,
            filterType: 'text',
            render: renderNestedField<T>(accessor),
            ...options,
        });
        return this;
    }

    /**
     * Add a timestamp column (auto-formatted)
     *
     * @param key - The field key from the data object
     * @param label - The column header label
     * @param options - Additional column options to override defaults
     */
    timestamp(key: keyof T | string, label: string, options?: Partial<Column<T>>): this {
        this.columns.push({
            key: key as string,
            label,
            sortable: true,
            filterable: false,
            render: renderTimestamp,
            ...options,
        });
        return this;
    }

    /**
     * Add a boolean column (Yes/No formatting)
     *
     * @param key - The field key from the data object
     * @param label - The column header label
     * @param filterPlaceholder - Filter placeholder (default: 'DA/NU')
     * @param options - Additional column options to override defaults
     */
    boolean(
        key: keyof T | string,
        label: string,
        filterPlaceholder: string = 'DA/NU',
        options?: Partial<Column<T>>
    ): this {
        this.columns.push({
            key: key as string,
            label,
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterPlaceholder,
            render: renderBoolean,
            ...options,
        });
        return this;
    }

    /**
     * Add a photo column
     *
     * @param key - The field key containing photo URL
     * @param label - The column header label
     * @param options - Additional column options to override defaults
     */
    photo(key: keyof T | string, label: string, options?: Partial<Column<T>>): this {
        this.columns.push({
            key: key as string,
            label,
            sortable: false,
            filterable: false,
            render: renderPhoto,
            ...options,
        });
        return this;
    }

    /**
     * Add a document/file column
     *
     * @param key - The field key containing document URL
     * @param label - The column header label
     * @param options - Additional column options to override defaults
     */
    document(key: keyof T | string, label: string, options?: Partial<Column<T>>): this {
        this.columns.push({
            key: key as string,
            label,
            sortable: false,
            filterable: false,
            render: renderDocument,
            ...options,
        });
        return this;
    }

    /**
     * Add an action column (buttons, links, etc.)
     *
     * @param key - Unique key for this action column
     * @param label - The column header label
     * @param renderFn - Function that renders the action UI for each row
     * @param options - Additional column options to override defaults
     */
    action(
        key: string,
        label: string,
        renderFn: (value: any, row: T) => ReactNode,
        options?: Partial<Column<T>>
    ): this {
        this.columns.push({
            key,
            label,
            sortable: false,
            filterable: false,
            render: renderFn,
            ...options,
        });
        return this;
    }

    /**
     * Add a custom column with full control
     *
     * Use this when you need complete control over the column definition
     * or when none of the convenience methods fit your use case.
     *
     * @param column - Complete column definition object
     */
    custom(column: Column<T>): this {
        this.columns.push(column);
        return this;
    }

    /**
     * Conditionally add columns
     *
     * Useful for role-based column rendering without breaking the chain.
     *
     * @param condition - Boolean condition to evaluate
     * @param builderFn - Function that adds columns when condition is true
     *
     * @example
     * ```tsx
     * columnBuilder<User>()
     *   .text('name', 'Name')
     *   .when(isAdmin, (b) => b
     *     .text('email', 'Email')
     *     .action('delete', 'Delete', row => <DeleteButton />)
     *   )
     *   .build();
     * ```
     */
    when(condition: boolean, builderFn: (builder: this) => this): this {
        if (condition) {
            return builderFn(this);
        }
        return this;
    }

    /**
     * Add multiple columns at once
     *
     * Useful when you have a dynamic array of columns to add.
     *
     * @param columnArray - Array of column definitions
     */
    addMany(columnArray: Column<T>[]): this {
        this.columns.push(...columnArray);
        return this;
    }

    /**
     * Build and return the final column array
     *
     * @returns Array of column definitions ready for DataTable
     */
    build(): Column<T>[] {
        return this.columns;
    }
}

/**
 * Factory function to create a new ColumnBuilder instance
 *
 * @template T - The type of data items in the table
 * @returns A new ColumnBuilder instance
 *
 * @example
 * ```tsx
 * const columns = columnBuilder<SchoolReportDto>()
 *   .text('name', 'Name')
 *   .build();
 * ```
 */
export function columnBuilder<T>(): ColumnBuilder<T> {
    return new ColumnBuilder<T>();
}
